import React, { useCallback, useEffect, useReducer, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
} from "@mui/material";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import QRCode from "react-qr-code";
import { lockedReducer } from '../../../utils/defaultReducer.js'
import ScoutSocketQRComponent from "../Pages/SocketQRComponent/index.js";
import './styles.css'

const IPV4_REGEX = /^\d+\.\d+\.\d+\.\d+$/;

const resolveScoutFrontend = () => {
  const { protocol, hostname, port, origin } = window.location;

  if (!hostname || IPV4_REGEX.test(hostname)) {
    return origin.replace(/\/$/, '');
  }

  const scoutHostname = hostname.startsWith('scout.') ? hostname : `scout.${hostname}`;
  const netloc = port ? `${scoutHostname}:${port}` : scoutHostname;

  return `${protocol}//${netloc}`;
};

const connectionSteps = [
  'No celular que será conectado, abra o WhatsApp.',
  'Acesse Configurações ou Menu, conforme a versão do aplicativo.',
  'Entre em Dispositivos conectados.',
  'Toque em Conectar dispositivo.',
  'Clique em Gerar conexão nesta tela.',
  'Use a opção QR code ou toque em Conectar com número de telefone e informe o código exibido.',
  'Aguarde a sincronização terminar para liberar o acesso.',
];

const statusMap = {
  idle: 'Pronto para gerar',
  generating: 'Gerando QR code',
  waiting: 'Aguardando leitura',
  connected: 'Conectado com sucesso',
  'wrong-number': 'Número incorreto',
  expired: 'Convite expirado',
};

const getPairingCodeFromResponse = (data = {}) => (
  data.pairingCode ||
  data.PairingCode ||
  data.qrcode?.pairingCode ||
  data.qrCode?.pairingCode ||
  ''
);

const getQrCodeFromResponse = (data = {}) => (
  data.code ||
  data.Code ||
  data.qrcode?.code ||
  data.qrCode?.code ||
  ''
);

const formatPairingCode = (code = '') => (
  String(code)
    .replace(/[\s-]/g, '')
    .replace(/(.{4})(?=.)/g, '$1-')
);

const ScanQRForm = () => {
  const pollingRef = useRef(null);
  const scoutAppPath = `${resolveScoutFrontend()}/home`;

  const [state, setState] = useReducer(lockedReducer, {
    showAlert: [],
    loading: false,
    qrCode: null,
    pairingCode: '',
    pairingError: '',
    socket: null,
    connectionStatus: 'idle',
    successModalOpen: false,
  });

  const urlParams = window.location.pathname.split("/");
  const enterpriseId = urlParams[urlParams.length - 6];
  const accountId = urlParams[urlParams.length - 5];
  const uid = urlParams[urlParams.length - 4];
  const accountcode = urlParams[urlParams.length - 3];
  const phone_number = urlParams[urlParams.length - 2];
  const token = urlParams[urlParams.length - 1];

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const checkConnection = useCallback(async () => {
    try {
      const res = await fetch(
        `/api/api/enterprises/${enterpriseId}/scout_accounts/${accountId}/checkconnection/`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        },
      );

      if (res.status !== 200) {
        return;
      }

      const data = await res.json();

      if (data.state === 'open') {
        stopPolling();
        setState({
          showAlert: [],
          connectionStatus: 'connected',
          loading: false,
          successModalOpen: true,
        });
        return;
      }

      if (data.state === 'wrongNumber') {
        stopPolling();
        setState({
          showAlert: [{
            severity: 'error',
            message: 'Número incorreto. Use o celular cadastrado para escanear o QR code.',
          }],
          qrCode: null,
          pairingCode: '',
          pairingError: '',
          loading: false,
          connectionStatus: 'wrong-number',
        });
        return;
      }

      setState({ connectionStatus: 'waiting' });
    } catch (error) {
      console.error(error);
    }
  }, [accountId, enterpriseId, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollingRef.current = setInterval(() => {
      checkConnection();
    }, 5 * 1000);
  }, [checkConnection, stopPolling]);

  const handleOpenScout = useCallback(() => {
    window.location.assign(scoutAppPath);
  }, [scoutAppPath]);

  const handleBtnClick = useCallback(async () => {
    console.log('[Scout pairing][self-service] requesting connection codes', {
      enterpriseId,
      accountId,
      phoneNumber: phone_number,
      accountcode,
    });

    setState({
      loading: true,
      showAlert: [],
      pairingError: '',
      connectionStatus: 'generating',
    });

    try {
      const res = await fetch(
        `/api/api/enterprises/${enterpriseId}/scout_accounts/${accountId}/getqr/`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            uid,
            token,
          }),
        },
      );

      if (res.status === 200) {
        const data = await res.json();
        const qrCode = getQrCodeFromResponse(data);
        const pairingCode = getPairingCodeFromResponse(data);

        console.log('[Scout pairing][self-service] getqr response', {
          status: res.status,
          data,
          parsed: {
            hasQrCode: Boolean(qrCode),
            pairingCode,
          },
        });

        setState({
          qrCode,
          pairingCode,
          pairingError: pairingCode ? '' : 'Não conseguimos gerar o código para conectar. Por favor, tente novamente em alguns instantes.',
          loading: false,
          connectionStatus: 'waiting',
        });
        startPolling();
        checkConnection();
        return;
      }

      console.warn('[Scout pairing][self-service] getqr returned non-200 status', { status: res.status });
      stopPolling();
      setState({
        loading: false,
        pairingError: 'Não foi possível gerar o código de pareamento.',
        connectionStatus: 'expired',
        showAlert: [{
          severity: 'error',
          message: 'Convite expirado. Solicite um novo envio para continuar.',
        }],
      });
    } catch (error) {
      console.error(error);
      stopPolling();
      setState({
        loading: false,
        pairingError: 'Não conseguimos gerar o código para conectar. Por favor, tente novamente em alguns instantes.',
        connectionStatus: 'idle',
        showAlert: [{
          severity: 'error',
          message: 'Não foi possível gerar o QR code agora. Tente novamente em instantes.',
        }],
      });
    }
  }, [accountId, checkConnection, enterpriseId, startPolling, stopPolling, token, uid]);

  const copyPairingCode = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(String(state.pairingCode).replace(/[\s-]/g, ''));
      setState({
        showAlert: [{
          severity: 'success',
          message: 'Código de pareamento copiado.',
        }],
      });
    } catch (error) {
      console.error(error);
      setState({
        showAlert: [{
          severity: 'error',
          message: 'Não foi possível copiar o código. Selecione o código na tela e copie manualmente.',
        }],
      });
    }
  }, [state.pairingCode]);

  useEffect(() => () => {
    stopPolling();
  }, [stopPolling]);

  return (
    <Box className="scan-scout-page">
      <Dialog
        open={state.successModalOpen}
        onClose={() => setState({ successModalOpen: false })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Número conectado com sucesso
        </DialogTitle>
        <DialogContent>
          <Typography>
            O WhatsApp já foi vinculado ao Kwik Scout. Você pode seguir direto para o painel para acompanhar as conversas e configurações.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            type="button"
            onClick={() => setState({ successModalOpen: false })}
            className="scan-scout-button scan-scout-button--secondary"
          >
            Fechar
          </Button>
          <Button
            type="button"
            onClick={handleOpenScout}
            className="scan-scout-button scan-scout-button--primary"
            autoFocus
          >
            Ir para o Kwik Scout
          </Button>
        </DialogActions>
      </Dialog>

      <ScoutSocketQRComponent account={{
        id: accountId,
        phone_number: phone_number,
        accountcode: accountcode,
      }} parent={state} setParent={setState}/>
      <Box className="scan-scout-card">
        <Box className="scan-scout-card__header">
          <Box>
            <Typography className="scan-scout-card__title">
              Conectar WhatsApp ao Kwik Scout
            </Typography>
            <Typography className="scan-scout-card__description">
              Gere uma conexão e escolha se prefere escanear o QR code ou conectar usando o número de telefone.
            </Typography>
          </Box>
          <Box className="scan-scout-status-pill">
            {statusMap[state.connectionStatus] || statusMap.idle}
          </Box>
        </Box>

        <Box className="scan-scout-phone-box">
          <Typography className="scan-scout-phone-box__label">
            Número vinculado ao convite
          </Typography>
          <Typography className="scan-scout-phone-box__value">
            {phone_number}
          </Typography>
        </Box>

        {state.showAlert.length !== 0 && (
          <Box className="scan-scout-alerts">
            {state.showAlert.map((alert) => (
              <Alert key={`${alert.severity}-${alert.message}`} severity={alert.severity} onClose={() => setState({ showAlert: [] })}>
                {alert.message}
              </Alert>
            ))}
          </Box>
        )}

        <Box className="scan-scout-content">
          <Box className="scan-scout-instructions">
            <Typography className="scan-scout-section-title">
              Como conectar
            </Typography>
            <List className='scout-informative-list'>
              {connectionSteps.map((step, index) => (
                <ListItem key={step} className="scan-scout-step">
                  <span className="scan-scout-step__index">{index + 1}</span>
                  <span className="scan-scout-step__text">{step}</span>
                </ListItem>
              ))}
            </List>
          </Box>

          <Box className="scan-scout-qr-panel">
            <Box className="scan-scout-qr-panel__inner">
              <Typography className="scan-scout-option-title">
                Opção 1: QR code
              </Typography>
              {state.qrCode ? (
                <Box className="scan-scout-qr-frame">
                  <QRCode value={state.qrCode} size={190} />
                </Box>
              ) : (
                <Box className="scan-scout-qr-placeholder">
                  <QrCode2RoundedIcon />
                  <Typography className="scan-scout-qr-placeholder__title">
                    Conexão ainda não gerada
                  </Typography>
                  <Typography className="scan-scout-qr-placeholder__text">
                    Clique no botão abaixo para gerar QR code e código de pareamento.
                  </Typography>
                </Box>
              )}

              <Box className="scan-scout-pairing-card">
                <Typography className="scan-scout-pairing-card__label">
                  Opção 2: conectar com número
                </Typography>
                {state.pairingCode ? (
                  <>
                    <Typography className="scan-scout-pairing-card__code">
                      {formatPairingCode(state.pairingCode)}
                    </Typography>
                    <Typography className="scan-scout-pairing-card__hint">
                      No WhatsApp, toque em conectar com número de telefone e informe este código.
                    </Typography>
                    <Button
                      type="button"
                      onClick={copyPairingCode}
                      className="scan-scout-button scan-scout-button--secondary"
                      fullWidth
                    >
                      Copiar código
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography className="scan-scout-pairing-card__hint">
                      O código aparecerá quando clicar no botão "Gerar Conexão".
                    </Typography>
                    {state.pairingError && (
                      <Typography className="scan-scout-pairing-card__error">
                        {state.pairingError}
                      </Typography>
                    )}
                  </>
                )}
              </Box>

              <Typography className="scan-scout-qr-caption">
                {state.qrCode
                  ? 'A página atualiza automaticamente assim que a conexão for concluída.'
                  : 'Depois de gerar, escolha QR code ou conexão por número no WhatsApp do número convidado.'}
              </Typography>
            </Box>

            <Box className="scan-scout-actions">
              <Button
                disabled={state.loading}
                type="button"
                onClick={handleBtnClick}
                className="scan-scout-button scan-scout-button--primary"
                fullWidth
              >
                {state.loading && <CircularProgress size={18} className="scan-scout-button__loader" />}
                {state.qrCode || state.pairingCode ? 'Gerar nova conexão' : 'Gerar conexão'}
              </Button>
              {state.qrCode && (
                <Button
                  type="button"
                  onClick={checkConnection}
                  className="scan-scout-button scan-scout-button--secondary"
                  fullWidth
                >
                  Verificar conexão
                </Button>
              )}
              {state.connectionStatus === 'connected' && (
                <Button
                  type="button"
                  onClick={handleOpenScout}
                  className="scan-scout-button scan-scout-button--primary"
                  fullWidth
                >
                  Ir para o Kwik Scout
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ScanQRForm;
